import { parse, stringify } from 'query-string';

import {
  ESorting,
  IGetVillageListRequestSchema,
} from '../../node/repositories/countryside-search/entities/schemas/GetVillageListRequestSchema';
import {
  DEFAULT_ROAD_DISTANCE,
  DEFAULT_STATION_DISTANCE,
  EXCLUDED_FILTERS,
  ROAD_DISTANCES,
  SavedFiltersIds,
  SERP_COMMUNICATIONS,
  SERP_HEATING_TYPE,
  SERP_HOUSE_MATERIAL,
  SERP_WC_TYPE,
  STATION_DISTANCES,
} from '../interfaces/filters';
import { getDefaultVillageListRequest, TIndexableObject } from '../interfaces/villages';
import { ISavedFilter } from '../store/saved_filters/actions';
import { isDistanceDefault } from './filters_helpers';
import { generateDistanceLabel } from './generateDistanceLabel';
import { getBuilderFilterLabel } from './getBuilderFilterLabel';
import { getSortingKeys, getSortingNames } from './sorting';

export const sortNumberStrings = (value: (number | string)[]) =>
  value.sort((a, b) => {
    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b);
    } else if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    } else {
      return 0;
    }
  });

const incorrectFilter = <T>(value?: T) => value === undefined || (Array.isArray(value) && !value.length);

export const isArrayFilterDefault = <T>(value: T[], defaultValue: T[]) =>
  value.length === defaultValue.length && defaultValue.every(v => value.includes(v));

export const isFilterDefault = <T>(key: string, value: T) => {
  if (isDistanceDefault(key, value)) {
    return true;
  }

  /**
   * TODO удалить byContentScore после выпила эксперимента на https://cianru.atlassian.net/browse/PM-4608
   * и вернуть sorting: 'default' в DEFAULT_GET_VILLAGE_LIST_REQUEST (он там нужен только для этого)
   */
  if (key === 'sorting' && ['byContentScore', 'default'].includes(String(value))) {
    return true;
  }
  const defaultValue = getDefaultVillageListRequest()[key as keyof IGetVillageListRequestSchema];
  if (defaultValue === undefined || value === undefined) {
    return false;
  }
  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    return isArrayFilterDefault(value, defaultValue);
  }

  return String(defaultValue) === String(value);
};

export const seoObjectType = `${encodeURI('objectType[0]')}=5`;

export const stringifyFilters = (filters: IGetVillageListRequestSchema, excludeSeo: boolean = false): string =>
  Object.keys(filters)
    .map((key: keyof IGetVillageListRequestSchema) => {
      let value = filters[key];
      if (EXCLUDED_FILTERS.includes(key) || incorrectFilter(value) || isFilterDefault(key, value)) {
        return null;
      }

      if (typeof value === 'boolean') {
        value = Number(value);
      } else if (Array.isArray(value)) {
        sortNumberStrings(value);
      }

      return `${key}=${value}`;
    })
    .concat([excludeSeo ? null : seoObjectType])
    .sort()
    .filter(Boolean)
    .join('&');

export const generateQueryString = (
  filters: IGetVillageListRequestSchema,
  questionMark: boolean = true,
  excludeSeo: boolean = false,
) => {
  const qs = stringifyFilters(filters, excludeSeo);
  const base = questionMark ? '?' : '';

  return qs ? `${base}${qs}` : '';
};

export const tryParseJSON = (serializedArray: string) => serializedArray.split(',');

export const createFromToString = (from?: number | null, to?: number | null) => {
  if (from !== undefined && to !== undefined) {
    return `от ${from} до ${to}`;
  } else if (from !== undefined) {
    return `от ${from}`;
  } else if (to !== undefined) {
    return `до ${to}`;
  }

  return '';
};

export const getSorting = (value: ESorting | undefined, regionHasRoads: boolean, regionHasStations: boolean) =>
  typeof value !== 'undefined' && value in getSortingNames({ regionHasRoads, regionHasStations })
    ? value
    : getSortingKeys().DEFAULT;

export const createSavedFilters = (
  filters: IGetVillageListRequestSchema,
  builderName?: string,
  addressName?: string,
): ISavedFilter[] => {
  const {
    regionId,
    saleStatus,
    readyStatus,
    otherCommunications,
    floorCountFrom,
    floorCountTo,
    areaFrom,
    areaTo,
    houseMaterialType,
    heatingSourceType,
    wcLocationType,
    builderId,
    commuteDistanceType,
    commuteKmFrom,
    commuteKmTo,
    blackFriday,
  } = filters;

  return [
    addressName && {
      id: 0,
      name: addressName,
    },
    saleStatus &&
      saleStatus.map(status => ({
        id: 1,
        name: status,
      })),
    readyStatus &&
      readyStatus.map(status => ({
        id: 2,
        name: status,
      })),
    otherCommunications &&
      otherCommunications.map(communicate => ({
        id: 3,
        name: communicate,
      })),
    houseMaterialType &&
      houseMaterialType.map(material => ({
        id: 4,
        name: material,
      })),
    floorCountFrom !== undefined || floorCountTo !== undefined
      ? {
          id: 5,
          name: `Этажей в доме ${createFromToString(floorCountFrom, floorCountTo)}`,
        }
      : undefined,
    areaFrom !== undefined || areaTo !== undefined
      ? {
          id: 6,
          name: `Площадь ${createFromToString(areaFrom, areaTo)} м²`,
        }
      : undefined,
    builderId !== undefined
      ? {
          id: 7,
          name: getBuilderFilterLabel(builderName, builderId as number),
        }
      : undefined,
    heatingSourceType &&
      heatingSourceType.map(name => ({
        id: 8,
        name,
      })),
    wcLocationType
      ? {
          id: 9,
          name: wcLocationType,
        }
      : undefined,
    commuteKmFrom !== undefined || commuteKmTo !== undefined
      ? {
          id: SavedFiltersIds.CommuteDistance,
          name: `${createFromToString(commuteKmFrom, commuteKmTo)} км ${generateDistanceLabel(
            commuteDistanceType ?? undefined,
            regionId ?? undefined,
          )}`,
        }
      : undefined,
    blackFriday && {
      id: SavedFiltersIds.BlackFriday,
      name: 'Чёрная пятница',
    },
  ].reduce((acc, val) => (val ? acc.concat(val) : acc), [] as ISavedFilter[]);
};

export interface ISerpResult {
  [key: string]: string | string[];
}

export const forwardSerpFilters = (filters: IGetVillageListRequestSchema): ISerpResult => {
  const result: ISerpResult = {};
  const { heatingSourceType, houseMaterialType, otherCommunications, wcLocationType } = filters;
  if (Array.isArray(heatingSourceType) && heatingSourceType.length) {
    result.heating_type = heatingSourceType.map(key => SERP_HEATING_TYPE[key]).sort();
  }
  if (Array.isArray(houseMaterialType) && houseMaterialType.length) {
    result.house_material = houseMaterialType.map(key => SERP_HOUSE_MATERIAL[key]).sort();
  }
  if (wcLocationType) {
    result.wc_site = SERP_WC_TYPE[wcLocationType];
  }
  if (Array.isArray(otherCommunications) && otherCommunications.length) {
    otherCommunications.forEach(key => (result[SERP_COMMUNICATIONS[key]] = '1'));
  }

  return result;
};

const DEFAULT_SERP_FILTERS = {
  deal_type: 'sale',
  engine_version: '2',
  offer_type: 'suburban',
};

export const generateVillageSerpLink = (filters: IGetVillageListRequestSchema) => {
  return (
    '/cat.php?' +
    encodeURI(
      stringify(
        {
          ...DEFAULT_SERP_FILTERS,
          ...forwardSerpFilters(filters),
        },
        { arrayFormat: 'index', encode: true },
      ),
    )
  );
};

export const parseQueryString = (qs: string | null | undefined) => (qs ? parse(qs, { arrayFormat: 'none' }) : null);

export const toLowerKeys = (o: TIndexableObject) => Object.keys(o).forEach(k => (o[k.toLowerCase()] = o[k]));

export const parseToNumberList = (text = '') => {
  const parsed = tryParseJSON(text);

  return [...new Set(parsed.map(Number).filter(Number))];
};

export const parseDistance = (distance: number, distances: number[], defaultDistance: number) =>
  distances.includes(distance) ? distance : defaultDistance;

export const parseRoadDistance = (text = '') => parseDistance(Number(text), ROAD_DISTANCES, DEFAULT_ROAD_DISTANCE);

export const parseStationDistance = (text = '') =>
  parseDistance(Number(text), STATION_DISTANCES, DEFAULT_STATION_DISTANCE);
